export type DeepPartial<T> = T extends object ? { [P in keyof T]: DeepPartial<T[P]> } : T

export type AskResponse = {
  answer: string | []
  citations: Citation[]
  generated_chart: string | null
  error?: string
  message_id?: string
  feedback?: MessageFeedback
  exec_results?: ExecResults[]
  agent_name: string
  sections?: { [key: string]: string[] }
}

export type Citation = {
  part_index?: number
  content: string
  id: string
  title: string | null
  filepath: string | null
  url: string | null
  metadata: string | null
  chunk_id: string | null
  reindex_id: string | null
}

export type ToolMessageContent = {
  citations: Citation[]
  intent: string
}

export type AzureSqlServerExecResult = {
  intent: string
  search_query: string | null
  search_result: string | null
  code_generated: string | null
  code_exec_result?: string | undefined
}

export type AzureSqlServerExecResults = {
  all_exec_results: AzureSqlServerExecResult[]
}

export type ChatMessage = {
  id: string
  role: string
  content: string | [{ type: string; text: string }, { type: string; image_url: { url: string } }]
  end_turn?: boolean
  date: string
  feedback?: MessageFeedback
  context?: string
  agent_name: string
  sections?: { [key: string]: string[] }
  error?: ChatErrorResponse
  message_type?: string
}

export enum ChatErrorCode {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CONTENT_FILTERED = 'CONTENT_FILTERED',
  AI_SERVICE_ERROR = 'AI_SERVICE_ERROR',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum ChatContentFilterReason {
  VIOLENCE = 'violence',
  HATE = 'hate',
  SELF_HARM = 'self_harm',
  SEXUAL = 'sexual',
}

export type ChatErrorResponse = {
  code: ChatErrorCode
  message: string
  filter_reason?: ChatContentFilterReason
}

export type ExecResults = {
  intent: string
  search_query: string | null
  search_result: string | null
  code_generated: string | null
}

export enum ChangeSourceEnum {
  AI_LAST_MSG = 'ai_last_msg',
  AI_PAST_MSG = 'ai_past_msg',
  MANUAL = 'manual',
}

export type FormField<T> = {
  field_value?: T | null
  change_source?: ChangeSourceEnum
  required?: boolean
  hidden?: boolean
  disabled?: boolean
  options?: T[] | null
  errors?: string[]
}

export type File = {
  id: string
  filename: string
  content_type: string
  url?: string
}

export type PlanningBasics = {
  updated_by_name: FormField<string>
  is_e_at_s_experiment: FormField<boolean>
  experiment_code: FormField<string>
  experiment_status: FormField<string>
  country: FormField<string>
  ou: FormField<string>
  mto_initiative: FormField<string> | null
  application_area: FormField<string> | null
  key_question: FormField<string> | null
  hypothesis_template: FormField<string> | null
  hypothesis: FormField<string>
  global_charter: FormField<string>
  local_charter: FormField<string> | null
  experiment_phase_high_level: FormField<string>
  dme_and_resources_committed: FormField<boolean>
  methodology: FormField<string>
  leading_kpi: FormField<string>
  global_category_name: FormField<string>
  brand: FormField<string>
  bottler: FormField<string>
  quarter: FormField<string>
  year: FormField<string>
  action_statement: FormField<string>
  gla_question: FormField<string>
}

export type SetupDetails = {
  experiment_start_date: FormField<string>
  experiment_end_date: FormField<string>
  final_result_submission_date: FormField<string>
  experiment_budget: FormField<number>
  analytical_team: FormField<string>
  campaign_name: FormField<string>
  campaign_objective: FormField<string>
  platform_partner: FormField<string>
  format: FormField<string>
  buying_type: FormField<string>
  exclusions: FormField<string>
  creatives: FormField<File[]>
  overall_test_structure_and_design: FormField<File[]>
  media_plan: FormField<File[]>
  budget_sufficiency_tool: FormField<File[]>
  mid_campaign_report: FormField<File[]>
  learning_code: FormField<string>
  learning_name: FormField<string>
  additional_learning: FormField<string>
}

export type Completion = {
  hypothesis_proven: FormField<boolean>
  confidence_in_results: FormField<string>
  total_experiment_dme_spend: FormField<number>
  additional_comments: FormField<string>
  upload_result_deck: FormField<File[]>
  other_supporting_data: FormField<File[]>
}

export type ExtraData = {
  id: FormField<number>
  title: FormField<string>
  readiness_score_for_chart: FormField<number>
  value_score_for_chart: FormField<number>
  updated_by_id: FormField<number>
  folder_path: FormField<string>
  high_value: FormField<string>
  approval: FormField<boolean>
  segmentation_category: FormField<string>
}

export type Experiment = {
  planning_basics: PlanningBasics
  setup_details: SetupDetails
  completion: Completion
  extra_data: ExtraData
}

export type ExtendedData = {
  experiment?: Experiment | null
  experiment_id?: string
  experiment_form_step?: string | null
  experiment_form_expanded: boolean
  user_confirmed_experiment_creation?: boolean | null
  user_initiated_form?: boolean | null
  keep_validating?: boolean
  view_only?: boolean
  experiment_deleted?: boolean
  user_requested_new_conversation?: boolean | null
}

export type Conversation = {
  id: string
  title: string
  messages: ChatMessage[]
  created_at: string
  extended_data: ExtendedData
  selected_agent: string | null
  pinned?: boolean
  updated_by?: {
    id: string
    name: string
  }
}

export type ConversationExtendedData = {
  user_confirmed_experiment_creation: boolean | null
}

export enum ChatCompletionType {
  ChatCompletion = 'chat.completion',
  ChatCompletionChunk = 'chat.completion.chunk',
}

export type ChatResponseChoice = {
  messages: ChatMessage[]
}

export type ChatResponse = {
  id: string
  model: string
  created: number
  object: ChatCompletionType
  choices: ChatResponseChoice[]
  conversation: Conversation
  error?: any
  selected_agent: string | null
}

export type ConversationRequest = {
  messages: ChatMessage[]
}

export enum UserRole {
  ADMIN = 'admin',
  DECODER = 'decoder',
  OU_ORCHESTRATOR = 'ou_orchestrator',
  EDITOR = 'editor',
  VIEWER = 'viewer',
}

export enum CreateExperimentBlockedReason {
  MISSING_SHAREPOINT_ACCOUNT = 'missing_sharepoint_account',
  INSUFFICIENT_PERMISSION = 'insufficient_permission',
}

export type UserPermissions = {
  create_experiment: boolean
  edit_experiment: boolean
  delete_experiment: boolean
  access_request_management: boolean
  user_management: boolean
  learning_decoding_management: boolean
}

export type User = {
  id: string
  name?: string
  email: string
  role: UserRole
  permissions?: UserPermissions
  ou?: string
  organization_name?: string
  function?: string
  create_experiment_blocked_reason?: CreateExperimentBlockedReason | null
}

export type UserCreatePayload = Omit<User, 'id' | 'permissions'> & {
  name: string
  email: string
  organization_name: string
  function: string
  ou: string
  role: UserRole
}

export type UserUpdatePayload = Partial<Omit<User, 'id' | 'permissions' | 'name' | 'email'>>

export type FieldValidationError = {
  field: string
  message: string
  type: string
}

export type UserResponse = {
  success: boolean
  user?: User
  message?: string
  errors?: FieldValidationError[]
}

export type UserDeleteResponse = {
  success: boolean
  message?: string
}

export type UserListParams = {
  page?: number
  page_size?: number
  search?: string
  filters?: {
    role?: string[]
    ou?: string
    organization_name?: string
    function?: string
  }
}

export type UserListResponse = {
  users: User[]
  total: number
  page: number
}

export type UserInfo = {
  access_token: string
  expires_on: string
  id_token: string
  provider_name: string
  user_claims: any[]
  user_id: string
}

export enum CosmosDBStatus {
  NotConfigured = 'CosmosDB is not configured',
  NotWorking = 'CosmosDB is not working',
  InvalidCredentials = 'CosmosDB has invalid credentials',
  InvalidDatabase = 'Invalid CosmosDB database name',
  InvalidContainer = 'Invalid CosmosDB container name',
  Working = 'CosmosDB is configured and working',
}

export type CosmosDBHealth = {
  cosmosDB: boolean
  status: string
}

export enum ChatHistoryLoadingState {
  Loading = 'loading',
  Success = 'success',
  Fail = 'fail',
  NotStarted = 'notStarted',
}

export type ErrorMessage = {
  title: string
  subtitle: string
}

export type UI = {
  title: string
  chat_title: string
  chat_description: string
  logo?: string
  chat_logo?: string
  show_share_button?: boolean
  show_chat_history_button?: boolean
}

export type Auth = {
  enabled: boolean
  authority: string
  client_id: string
  scopes: string[]
}

export type AuthInitialConfig = {
  auth?: Auth
}

export type Template = {
  select_gla_strategic_priority: string
  experiment_budget: string
  overall_test_structure_and_design: string
  budget_sufficiency_tool: string
  mid_campaign_report: string
  total_experiment_dme_spent: string
  upload_result_deck: string
}

export type Support = {
  name: string
  email: string
}

export type AppSecret = {
  enabled: boolean
  header_value: string
}

export type MicroLearningSettings = {
  enabled: boolean
  title_min_length: number
  title_max_length: number
  summary_min_length: number
  summary_max_length: number
  recommended_actions_min: number
  recommended_actions_max: number
  recommended_action_min_length: number
  history_page_size: number
}

export type FrontendSettings = {
  auth?: Auth
  feedback_enabled?: string | null
  ui?: UI
  sanitize_answer?: boolean
  template?: Template
  support?: Support
  app_secret?: AppSecret
  build?: Build
  urls?: Urls
  ws?: WebSocketSettings
  micro_learning?: MicroLearningSettings
}

export type WebSocketSettings = {
  timeout: number
}

export type Urls = {
  experiments_dashboard: string
}

export type Build = {
  version: string
  timestamp: string
}

export enum FeedbackType {
  Neutral = 'neutral',
  Positive = 'positive',
  Negative = 'negative',
  MissingCitation = 'missing_citation',
  WrongCitation = 'wrong_citation',
  OutOfScope = 'out_of_scope',
  InaccurateOrIrrelevant = 'inaccurate_or_irrelevant',
  OtherUnhelpful = 'other_unhelpful',
  HateSpeech = 'hate_speech',
  Violent = 'violent',
  Sexual = 'sexual',
  Manipulative = 'manipulative',
  OtherHarmful = 'other_harmlful',
}

export type MessageFeedback = {
  type?: FeedbackType
  details?: string
}

export type ExperimentsListProps = {
  filters: string
  search?: string
  projection?: string
  sortBy: string
  sortOrder: string
  page: number
  pageSize: number
}

export type ExperimentsListResponse = {
  items: ExperimentListItem[]
  total: number
}

export type ExperimentListItem = {
  id: string
  created_at?: string
  updated_at?: string
  data: DeepPartial<Experiment>
}

export type ExperimentFiltersOptions = {
  year: string[]
  status: string[]
  phase: string[]
  ou: string[]
  country: {
    name: string
    ou: string
  }[]
  global_charter: string[]
  mto_initiative: string[]
  gla_strategic_priority: string[]
  brand: string[]
  methodology: string[]
  platform: string[]
  application_area: string[]
}

export type RoleChangeRequestPayload = {
  organization_name: string
  ou: string
  function: string
  request_reason: string
}

export type RoleChangeRequestResponse = {
  success: boolean
  request_id?: string
  error?: string
}

export type RoleChangeRequestOptions = {
  options: {
    ou: string[]
  }
}

export type PendingRequest = {
  id: string
  user: Pick<User, 'name' | 'email'>
  ou: string
  function: string
  request_reason: string
  created_at: string
}

export type PendingRequestsResponse = {
  requests: PendingRequest[]
  total: number
}

export type ProcessedRequest = {
  id: string
  user: Pick<User, 'name' | 'email'>
  request_reason: string
  status: 'approved' | 'rejected'
  notes: string | null
  processed_by: Pick<User, 'name' | 'email'>
  created_at: string
  updated_at: string
}

export type RequestHistoryResponse = {
  requests: ProcessedRequest[]
  total: number
}

export type RequestHistoryFilters = {
  page?: number
  page_size?: number
  status?: 'approved' | 'rejected'
  processed_by_user_id?: string
  start_date?: string
  end_date?: string
  sort_by?: string
  sort_order?: number
  ou?: string
}

export type ApproverOption = {
  id: string
  name: string
  email: string
}

export type FilterOptions = {
  approvers: ApproverOption[]
  ous: string[]
}

export type FilterOption = {
  value: string
  label: string
}

export type UserFilterOptions = {
  users: FilterOption[]
  roles: FilterOption[]
  actions: FilterOption[]
}

// ==================== USER HISTORY TYPES ====================

export type UserLogInfo = {
  id: string
  name: string
  email: string
}

export type UserLog = {
  id: string
  action_type: string
  target: UserLogInfo
  processed_by: UserLogInfo
  changes: Record<string, any> | null
  created_at: string
}

export type UserHistoryParams = {
  page?: number
  page_size?: number
  search?: string
  action_type?: string
  processed_by_user_id?: string
  start_date?: string
  end_date?: string
}

export type UserHistoryResponse = {
  logs: UserLog[]
  total: number
}

export type ExperimentLogsParams = {
  experiment_id: string
  range?: string
}

export type ExperimentLogsResponse = {
  history: ExperimentLog[]
}

export type ExperimentLog = {
  user_name: string
  log_description: string
  log_timestamp: string
}

export type ExperimentLogsRangesResponse = {
  ranges: ExperimentLogsRange[]
}

export type ExperimentLogsRange = {
  range_key: string
  label: string
  is_disabled: boolean
}

// ==================== APPLICATION ANALYTICS TYPES ====================

export type WeeklyMetricPoint = {
  year: number
  week: number
  count: number
}

export type WeeklyBreakdownRow = {
  user_id: string
  name?: string | null
  email?: string | null
  total: number
  weekly_counts: WeeklyMetricPoint[]
}

export type ApplicationAnalyticsFilters = {
  roles: string[]
  weeks: number
  range_start: string
  range_end: string
}

export type ApplicationAccessMetrics = {
  active_users_last_period: number
  total_accesses: number
  adoption_percentage: number
  registered_users_current: number
  weekly_access: WeeklyMetricPoint[]
  weekly_breakdown: WeeklyBreakdownRow[]
}

export type ApplicationUsageMetrics = {
  weekly_usage: WeeklyMetricPoint[]
  weekly_breakdown: WeeklyBreakdownRow[]
}

export type ApplicationAnalyticsResponse = {
  filters: ApplicationAnalyticsFilters
  app_access: ApplicationAccessMetrics
  app_usage: ApplicationUsageMetrics
}

export enum NotificationType {
  ACCESS_REQUEST_CREATED = 'access_request_created',
  ACCESS_REQUEST_APPROVED = 'access_request_approved',
  ACCESS_REQUEST_REJECTED = 'access_request_rejected',
  EXPERIMENT_COMPLETED = 'experiment_completed',
}

export type Notification = {
  id: string
  user_id: string
  type: NotificationType
  title: string
  message: string
  parent_id: string | null
  is_read: boolean
  created_at: string
  updated_at: string
}

export type NotificationsResponse = {
  notifications: Notification[]
}

export type UnreadNotificationsCountResponse = {
  unread_count: number
}

export type AdoptionDashboardFiltersResponse = [
  {
    quarter: string
    year: number
  },
]

export type PeriodItem = {
  quarter: string
  year: number
}

export type OUColumn = {
  key: string
  label: string
}

export type SummaryByOURow = {
  title: string
  decoded_learning: string
  launched_quarter: string | null
  launched_year: number | null
  ou_statuses: Record<string, string | null>
}

export type SummaryByOUResponse = {
  rows: SummaryByOURow[]
}

export type SummaryByOUFiltersResponse = {
  periods: PeriodItem[]
  titles: string[]
  decoded_learnings: string[]
  ou_columns: OUColumn[]
}

export type ViewEditAdoptionStatusRow = {
  id: string
  ou?: string | null
  market?: string | null
  mto_initiative?: string | null
  bpd_launched_in?: string | null
  bpd_name?: string | null
  status?: string | null
  indicate_activation_month?: string | null
  eligible_mkt?: string | null
  blocker_comment?: string | null
  bpd_activation_status?: string | null
  indicate_tentative_month?: string | null
  brands?: string[]
  charters?: string[]
  year?: number | null
  quarter?: string | null
  has_blocker_comments?: boolean
}

export type ViewEditAdoptionStatusListResponse = {
  items: ViewEditAdoptionStatusRow[]
}

export type ViewEditAdoptionStatusFiltersResponse = {
  mto_initiatives: string[]
  ous: string[]
  bpds: string[]
}

export type FreezeYTDResponse = {
  count: number
}

export type AdoptionDashboardDataResponse = {
  avg_global_percentage_scaled: number | null
  avg_global_target_percentage_scaled: number | null
  avg_global_adoption_wo_sustainability_scaled: number | null
  num_new_decoded_learnings: number | null
  groups: Array<{
    year: number
    avg_global_percentage: number | null
    avg_global_target_percentage: number | null
    avg_global_adoption_wo_sustainability: number | null
    bpds: Array<{
      name: string
      launched_in: {
        quarter: string | null
        year: number | null
      }
      target_period_percentage: number | null
      global_adoption_wo_sustainability: number | null
      target_percentage: number | null
    }>
  }>
}

// ==================== MICRO-LEARNINGS TYPES ====================

export type MicroLearningsListParams = {
  search?: string
  status?: string
  sort_by?: string
  sort_order?: string
  page?: number
  per_page?: number
  ou?: string
  area?: string
  gla_strategic_priority?: string
  has_scale_potential?: boolean
}

export type MicroLearningFilterOptions = {
  ou: string[]
  area: string[]
  gla_strategic_priority: string[]
}

export type ScoreTooltip = {
  experiment_codes: string[]
  without_sales_evidence: string[]
  missing_quantitative_results: string[]
  kpi_mismatch: boolean
}

export type ScoreBreakdown = {
  d1_base: number
  d1_quantitative_penalty: number
  d1_kpi_penalty: number
  d1_total: number
  d2_total: number
}

export type MicroLearningScore = {
  total: number
  tier: 'high' | 'medium' | 'low'
  breakdown: ScoreBreakdown
  tooltip: ScoreTooltip
}

export type MicroLearningStatusType = 'pending_review' | 'approved' | 'dismissed' | 'not_processed' | 'regenerating'

export type FailureReason = 'missing_result_deck' | 'rag_not_ready' | 'insufficient_content'

export type FailureDetail = {
  experiment_code: string
  reason: FailureReason | string
  details: string
}

export type MicroLearningProcessing = {
  status?: string
  failure_reason?: string | null
  failure_details?: FailureDetail[]
} & Record<string, any>

export type MicroLearningExportFormat = 'xlsx' | 'pptx'

export type FeedbackCategoryId =
  | 'framing'
  | 'too_narrow'
  | 'too_broad'
  | 'weak_recs'
  | 'oversimplified'
  | 'weak_synthesis'
  | 'tone'
  | 'other'

export type FeedbackCategory = {
  id: FeedbackCategoryId
  label: string
  hint: string
}

export const FEEDBACK_CATEGORIES: FeedbackCategory[] = [
  { id: 'framing', label: 'Wrong framing or angle', hint: 'Describe what the lead finding should be' },
  {
    id: 'oversimplified',
    label: 'Missing nuance from experiments',
    hint: 'What nuance or distinction was missed',
  },
  { id: 'tone', label: 'Tone or format needs adjustment', hint: 'Describe the desired tone or format change' },
  {
    id: 'weak_recs',
    label: "Recommendations aren't actionable",
    hint: 'What kind of actions or specificity would be more useful',
  },
  { id: 'too_broad', label: 'Scope too broad', hint: 'What should the micro-learning focus on specifically' },
  { id: 'too_narrow', label: 'Scope too narrow', hint: 'Which dimensions should be included' },
  {
    id: 'weak_synthesis',
    label: 'Cross-experiment synthesis is weak',
    hint: "What's the unifying thread the AI should draw out",
  },
  { id: 'other', label: 'Other', hint: 'Describe what needs to be changed' },
]

export type RegenerationRequest = {
  feedback: {
    categories: FeedbackCategoryId[]
    text: string
  }
}

export type MicroLearningListItem = {
  ml_id: string
  title: string
  grouping_rationale?: string | null
  gla_strategic_priority: string[]
  ous: string[]
  score: MicroLearningScore
  status: MicroLearningStatusType
  created_at: string
  experiment_count: number
  pipeline_experiments_count: number
  potential_other_learnings?: boolean
  experiments_scale_potential?: Array<{ id: string; status?: string | null }>
}

export type MicroLearningsListResponse = {
  data: MicroLearningListItem[]
  pagination: {
    page: number
    per_page: number
    total_items: number
    total_pages: number
  }
}

export type ReviewDecisionUser = {
  id: string
  name: string
}

export type DismissReasonId =
  | 'insufficient_evidence'
  | 'duplicate'
  | 'not_relevant'
  | 'low_quality'
  | 'outdated'
  | 'other'

export type ExperimentDisposition = 'recluster' | 'exclude'

export type ExperimentDispositionItem = {
  code: string
  disposition: ExperimentDisposition
}

export type DismissMicroLearningRequest = {
  selected_reason: DismissReasonId
  reason?: string | null
  experiment_dispositions: ExperimentDispositionItem[]
}

export type ReviewDecision = {
  action: string
  user: ReviewDecisionUser
  published_to_ous: string[]
  note?: string | null
  selected_reason?: DismissReasonId | null
  reason?: string | null
  experiment_dispositions?: ExperimentDispositionItem[]
  decided_at?: string | null
}

export type MicroLearningDetailExperimentMetadata = {
  country?: string | null
  ou?: string | null
  period?: string | null
  gla_strategic_priority?: string | null
  brand?: string | null
  global_charter?: string | null
  kpi?: string | null
  impressions?: string | null
  spend?: string | null
  cpm?: string | null
  hypothesis?: string | null
  hypothesis_proven?: string | null
  confidence_in_results?: string | null
}

export type MicroLearningDetailExperiment = {
  code: string
  id: string
  conclusive?: boolean
  status?: string
  missing_criteria?: string[]
  metadata?: MicroLearningDetailExperimentMetadata
}

export type AvailableExperimentItem = {
  code: string
  id: string
  available: boolean
  linked_ml_id: string | null
  ou?: string | null
  phase?: string | null
  country?: string | null
}

export type AvailableExperimentsResponse = {
  items: AvailableExperimentItem[]
  total: number
}

export type AddExperimentRequest = {
  experiment_code: string
}

export type ExperimentLinkTarget = 'experiments' | 'scale-potential'

export type ExperimentMutationResult = {
  success: boolean
  status: number | null
  error?: string
  message?: string
}

export type MutationResult = ExperimentMutationResult

export type MicroLearningDetailResponse = {
  ml_id: string
  title: string
  master_hypothesis: string
  score?: MicroLearningScore
  status: MicroLearningStatusType
  created_at?: string | null
  updated_at?: string | null
  ous: string[]
  gla_strategic_priority: string[]
  potential_other_learnings?: boolean
  has_newly_completed_experiments?: boolean
  experiments: MicroLearningDetailExperiment[]
  experiments_scale_potential: MicroLearningDetailExperiment[]
  generation?: {
    title?: string
    summary?: string
    key_findings?: string[]
    recommended_actions?: string[]
    limitations?: string
    next_stage_eligibility?: string
    data_gaps?: string[]
    bpd_readiness?: Record<string, any>
  } | null
  score_calculation?: {
    completed_count: number
    sales_lift_data: boolean
  }
  enrichment?: Record<string, any>
  processing?: MicroLearningProcessing
  review_decision?: ReviewDecision | null
  selection_rationale?: string | null
}

export type EditMicroLearningPayload = {
  title: string
  summary: string
  recommended_actions: string[]
}

export type EditMicroLearningResponse = {
  version: number
  new_status: string
}

export type MicroLearningLogAction =
  | 'generated'
  | 'edited'
  | 'approved'
  | 'dismissed'
  | 'refine_regen_requested'
  | 'regenerate_success'
  | 'regenerate_fail'

export type ChangeSource = 'manual' | 'ai_pipeline'

export type ActorInfo = {
  source: ChangeSource
  id?: string
  name: string
}

export type FieldChange = {
  field_name: string
  old_value?: unknown
  new_value?: unknown
}

export type ExperimentChange = {
  reclustered: string[]
  excluded: string[]
}

export type MicroLearningHistoryEntry = {
  ml_id: string
  action: MicroLearningLogAction
  actor: ActorInfo
  changed_fields?: string[]
  experiment_changes?: ExperimentChange
  feedback?: string | null
  note?: string | null
  created_at: string
}

export type SystemPrompt = {
  key: string
  name: string
  description: string
  content: string
  default_content: string
  template_variables: string[]
  version: number
  is_custom: boolean
  updated_by?: string | null
  updated_at?: string | null
  created_at: string
}
